import React, { useState, useEffect, useRef } from 'react';
import { formatDateToString, parseDateString } from '../../utils/dateUtils';

const DateRangePicker = ({ checkInDate, checkOutDate, onDateChange, minDate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectingCheckOut, setSelectingCheckOut] = useState(false);
  const pickerRef = useRef(null);

  // Resolve theme dynamically based on current URL path
  const getTheme = () => {
    const path = window.location.pathname;
    if (path.includes('/admin')) {
      return {
        focusRing: 'focus-within:border-red-500 focus-within:ring-2 focus-within:ring-red-500',
        textAccent: 'text-red-600',
        dayHover: 'hover:bg-red-50 hover:text-red-600',
        daySelected: 'bg-red-500 text-white hover:bg-red-600 shadow-md',
        dayRange: 'bg-red-50 text-red-600',
        iconColor: 'text-red-500',
        linkColor: 'text-red-500 hover:text-red-600'
      };
    } else if (path.includes('/support')) {
      return {
        focusRing: 'focus-within:border-green-500 focus-within:ring-2 focus-within:ring-green-500',
        textAccent: 'text-green-600',
        dayHover: 'hover:bg-green-50 hover:text-green-600',
        daySelected: 'bg-green-500 text-white hover:bg-green-600 shadow-md',
        dayRange: 'bg-green-50 text-green-600',
        iconColor: 'text-green-500',
        linkColor: 'text-green-500 hover:text-green-600'
      };
    }
    return {
      focusRing: 'focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500',
      textAccent: 'text-blue-600',
      dayHover: 'hover:bg-blue-50 hover:text-blue-600',
      daySelected: 'bg-blue-500 text-white hover:bg-blue-600 shadow-md',
      dayRange: 'bg-blue-50 text-blue-600',
      iconColor: 'text-blue-500',
      linkColor: 'text-blue-500 hover:text-blue-600'
    };
  };

  const theme = getTheme();

  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    // Use the utility function to parse date properly
    const date = parseDateString(dateStr);
    if (!date) return '';
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric'
    });
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const isDateInRange = (date) => {
    if (!checkInDate || !checkOutDate || !date) return false;
    // Use utility functions to avoid timezone issues
    const checkIn = parseDateString(checkInDate);
    const checkOut = parseDateString(checkOutDate);
    const currentDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    return currentDate >= checkIn && currentDate <= checkOut;
  };

  const isDateSelected = (date) => {
    if (!date) return false;
    const dateStr = formatDateToString(date);
    return dateStr === checkInDate || dateStr === checkOutDate;
  };

  const isDateDisabled = (date) => {
    if (!date) return true;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };
  const handleDateClick = (date) => {
    if (isDateDisabled(date)) return;
    
    // Use local date formatting instead of toISOString to avoid timezone issues
    const dateStr = formatDateToString(date);
    
    if (!checkInDate || (checkInDate && checkOutDate)) {
      onDateChange('checkInDate', dateStr);
      onDateChange('checkOutDate', '');
      setSelectingCheckOut(true);
    } else if (checkInDate && !checkOutDate) {
      const checkIn = parseDateString(checkInDate);
      if (date <= checkIn) {
        // Same date or before check-in: reset to select new check-in
        onDateChange('checkInDate', dateStr);
        onDateChange('checkOutDate', '');
      } else {
        onDateChange('checkOutDate', dateStr);
        setSelectingCheckOut(false);
        setIsOpen(false);
      }
    }
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle window resize and scroll to reposition dropdown
  useEffect(() => {
    const updateDropdownPosition = () => {
      if (isOpen && pickerRef.current) {
        const dropdown = document.querySelector('.date-picker-dropdown');
        if (dropdown) {
          const rect = pickerRef.current.getBoundingClientRect();
          const newTop = rect.bottom + 8;
          const newLeft = rect.left;
          
          dropdown.style.top = `${newTop}px`;
          dropdown.style.left = `${newLeft}px`;
        }
      }
    };

    if (isOpen) {
      // Update position immediately
      updateDropdownPosition();
      
      // Add event listeners for continuous updates
      window.addEventListener('scroll', updateDropdownPosition, { passive: true });
      window.addEventListener('resize', updateDropdownPosition, { passive: true });
      document.addEventListener('scroll', updateDropdownPosition, { passive: true, capture: true });
      
      return () => {
        window.removeEventListener('scroll', updateDropdownPosition);
        window.removeEventListener('resize', updateDropdownPosition);
        document.removeEventListener('scroll', updateDropdownPosition, { capture: true });
      };
    }
  }, [isOpen]);

  return (
    <div className="relative" ref={pickerRef} style={{ zIndex: 9999 }}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-4 py-3 bg-white border border-gray-300 rounded-lg ${theme.focusRing} cursor-pointer transition-all duration-200 hover:border-gray-400 shadow-sm`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <div>
              <div className="text-sm text-gray-900">
                {checkInDate && checkOutDate ? (
                  <span className="font-medium">{formatDisplayDate(checkInDate)} — {formatDisplayDate(checkOutDate)}</span>
                ) : checkInDate ? (
                  <span>{formatDisplayDate(checkInDate)} — <span className={theme.textAccent}>Select checkout</span></span>
                ) : (
                  <span className="text-gray-500">Select dates</span>
                )}
              </div>
            </div>
          </div>
          <svg className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {isOpen && (
        <div className="date-picker-dropdown fixed bg-white border border-gray-200 rounded-lg shadow-lg p-4 w-72"
             style={{ 
               zIndex: 10000,
               top: pickerRef.current ? pickerRef.current.getBoundingClientRect().bottom + 8 : 'auto',
               left: pickerRef.current ? pickerRef.current.getBoundingClientRect().left : 'auto'
             }}>
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={() => navigateMonth(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg text-gray-600 transition-colors duration-200"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h3 className="text-sm font-semibold text-gray-800">
              {months[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </h3>
            <button
              type="button"
              onClick={() => navigateMonth(1)}
              className="p-2 hover:bg-gray-100 rounded-lg text-gray-600 transition-colors duration-200"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-3">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
              <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {getDaysInMonth(currentMonth).map((date, index) => {
              if (!date) {
                return <div key={index} className="h-8"></div>;
              }

              const isSelected = isDateSelected(date);
              const isInRange = isDateInRange(date);
              const isDisabled = isDateDisabled(date);
              const isToday = date.toDateString() === new Date().toDateString();

              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleDateClick(date)}
                  disabled={isDisabled}
                  className={`
                    h-8 w-8 rounded-lg text-sm font-medium transition-all duration-200
                    ${isDisabled 
                      ? 'text-gray-300 cursor-not-allowed' 
                      : `${theme.dayHover} cursor-pointer`
                    }
                    ${isSelected 
                      ? theme.daySelected 
                      : ''
                    }
                    ${isInRange && !isSelected 
                      ? theme.dayRange 
                      : ''
                    }
                    ${isToday && !isSelected 
                      ? 'bg-gray-100 text-gray-900 font-bold border border-gray-300' 
                      : ''
                    }
                  `}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>

          <div className="mt-4 pt-3 border-t border-gray-200">
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span className="flex items-center">
                <svg className={`w-4 h-4 mr-2 ${theme.iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {selectingCheckOut ? 'Select check-out date' : 'Select check-in date'}
              </span>
              <button
                type="button"
                onClick={() => {
                  onDateChange('checkInDate', '');
                  onDateChange('checkOutDate', '');
                  setSelectingCheckOut(false);
                }}
                className={`${theme.linkColor} font-medium transition-colors duration-200`}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;